import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { FlowButtons, FlowConfig, StepConfig, StepperAdobeTag } from './flow-config.interface';
import { Store } from '@ngrx/store';
import { BreakpointObserverService } from '../../services/breakpoint-observer/breakpoint-observer.service';
import { ContentUtil } from '../../utils/content/content.util';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { resetGrantRevokePermissionState } from '../../store/actions/grant-revoke-permissions.action';
import { Router } from '@angular/router';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { AdobeLaunchService } from '../../services/adobe-launch/adobe-launch.service';
import { DrawerComponent, DrawerModule } from '@vg-constellation/angular-20/drawer';
import { VgnService } from '../../services/vgn/vgn.service';
import { adobeTagging } from './adobe-tagging-data';
import { FlowConfigService } from '../../services/flow-config-service/flow-config.service';
import { ModalDialogComponent, ModalDialogModule } from '@vg-constellation/angular-20/modal-dialog';
import { ClientSegmentFacadeService } from 'src/app/services/client-segment-observer/client-segment.service';
import { ClientSegmentContentDTO } from 'src/common/dtos/client-segment.dto';
import { FlowKey } from '../../enums/flow-keys.enum';
import { isInternalEnvironment } from 'src/common/utils/common.util';
import { AsyncPipe, NgClass, NgComponentOutlet, NgFor, NgIf } from '@angular/common';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import { IconModule } from '@vg-constellation/angular-20/icon';
import { StepIndicatorModule } from '@vg-constellation/angular-20/step-indicator';
import { AdvisorComponent } from '../representative-card/advisor/advisor.component';
import { DetailsModule } from '@vg-constellation/angular-20/details';
import { environment } from 'src/environments/environment';

const CLIENT_ONLY_ACTION_SCRIPT_ID = 'c11n-client-only-action-script';
const WEB_BUTTON_SCRIPT_ID = 'c11n-web-button-script';

export async function loadWebComponentScripts(): Promise<void> {
    await Promise.all([
        loadScript(CLIENT_ONLY_ACTION_SCRIPT_ID, environment.C11N_WEB_COMPONENTS_V1.CLIENT_ONLY_ACTION_URL),
        loadScript(WEB_BUTTON_SCRIPT_ID, environment.C11N_WEB_COMPONENTS_V1.BUTTON_URL),
    ]);
}

function loadScript(id: string, url: string): Promise<void> {
    if (document.getElementById(id) || (window as any)?.Cypress) {
        return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = id;
        script.type = 'module';
        script.src = url;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
        document.body.appendChild(script);
    });
}
@Component({
    selector: 'zci-stepper',
    templateUrl: './stepper.component.html',
    styleUrls: ['./stepper.component.scss'],
    imports: [
        AsyncPipe,
        NgIf,
        NgFor,
        NgClass,
        NgComponentOutlet,
        ButtonModule,
        IconModule,
        DrawerModule,
        ModalDialogModule,
        StepIndicatorModule,
        DetailsModule,
        AdvisorComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StepperComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly breakpointObserver = inject(BreakpointObserverService);
    private readonly adobeLaunchService = inject(AdobeLaunchService);
    private readonly vgnService = inject(VgnService);
    private readonly flowConfigService = inject(FlowConfigService);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);
    private renderer = inject(Renderer2);

    @Input() flow: FlowKey = FlowKey.TEST;
    isInternal = isInternalEnvironment();

    get stepperSizeAdjustingClasses(): string {
        if (!this.currentStepConfig.isStandaloneStep) {
            return this.flow === FlowKey.RAF ? 'col-md-10 col-xl-6 mx-auto raf-margin' : 'col-12 col-sm-12 col-md-12 col-lg-10 col-xl-10 col-xxl-10 mx-auto';
        }

        return '';
    }

    get shouldShowFlowCaptionAndFlowTopNav(): boolean {
        return !this.currentStepConfig.isStandaloneStep;
    }

    get showStepIndicator(): boolean {
        return this.currentStepConfig.isStepIndicatorNeeded;
    }

    flowConfig: FlowConfig;
    currentStepIndex: number = null;
    currentStepConfig: StepConfig = null;
    stepLabels: string[];
    buttons: FlowButtons = {
        contactUs: true,
        quit: true,
    };

    @ViewChild('quitButtonModalDialog', { static: false })
    quitButtonModalDialog!: ModalDialogComponent;
    @ViewChild('drawer', { static: false })
    drawerComponent?: DrawerComponent;
    @HostBinding('class.push')
    pushContent: boolean = false;
    @HostBinding('class.gaf-drawer-open')
    drawerOpen: boolean = false;

    isMobileView$: Observable<boolean> = this.breakpointObserver.isMobileView$;
    isTabletView$: Observable<boolean> = this.breakpointObserver.isTabletView$;
    isMediumHeightView$: Observable<boolean> = this.breakpointObserver.isMediumHeightView$;

    nextStepFromChild: EventEmitter<any> = new EventEmitter();
    nextStepClicked: EventEmitter<any> = new EventEmitter();
    locationChanged: EventEmitter<any> = new EventEmitter();

    isWaiting$: BehaviorSubject<any> = new BehaviorSubject(false);
    destroyed$: Subject<void> = new Subject();

    //TODO: move this content out from stepper component
    content = ContentUtil.grantorFragmentContent.add_user;

    contactPhone: string = ContentUtil.globalRMSupportContactPhoneNumber;
    phoneNumber: string;
    supportName: string;

    ngOnInit(): void {
        loadWebComponentScripts();
        this.loadFlow();
        this.vgnService.setMinimalNav(true);
        this.clientSegmentFacadeService.isWealthManagementClient$.subscribe((isClient: boolean) => {
            this.buttons = Object.assign(this.buttons, { contactUs: isClient });
            if (!isClient) {
                this.drawerComponent = undefined;
            }
        });
        this.clientSegmentFacadeService.clientSegmentContent$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((content: ClientSegmentContentDTO) => {
                this.phoneNumber = this.contactPhone.replace(
                    '${phoneNumber}',
                    content?.phoneNumber,
                );
                this.supportName = content?.supportTeam.replace(' team', '');
            });
    }

    ngOnDestroy(): void {
        this.vgnService.setMinimalNav(false);
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private loadFlow() {
        this.flowConfig = this.flowConfigService.getFlowConfig(this.flow);
        this.stepLabels = this.flowConfig.steps.filter(step => step.isStepIndicatorNeeded).map((item) => item.title);
        this.buttons = Object.assign(this.buttons, this.flowConfig?.buttons);
        this.currentStepIndex = 0;
        this.changeStep();
    }

    private changeStep(change = 0) {
        if (this.currentStepIndex + change < 0) {
            this.exitFlow();
            return;
        }

        if (this.currentStepIndex + change >= this.flowConfig.steps.length) {
            return;
        }


        this.isWaiting$.next(false);
        this.currentStepIndex += change;
        const stepConfig = this.flowConfig.steps[this.currentStepIndex];

        if (this.nextStepFromChild?.unsubscribe) {
            this.nextStepFromChild.unsubscribe();
        }

        if (this.locationChanged?.unsubscribe) {
            this.locationChanged.unsubscribe();
        }

        this.nextStepFromChild = new EventEmitter<boolean>();
        this.nextStepClicked = new EventEmitter<any>();
        this.locationChanged = new EventEmitter<any>();

        this.nextStepFromChild.pipe(takeUntil(this.destroyed$)).subscribe((response) => {
            this.isWaiting$.next(false);
            if (response) {
                this.changeStep(1);
            }
        });

        this.locationChanged
            .pipe(takeUntil(this.destroyed$))
            .subscribe((locationValue: string) =>
                this.updateCurrentStepConfigAdobeLocation(locationValue),
            );

        this.currentStepConfig = {
            ...stepConfig,
            inputs: {
                ...stepConfig.inputs,
                nextStepReady: this.nextStepFromChild,
                nextStepClicked: this.nextStepClicked,
                locationChanged: this.locationChanged,
            },
        };
    }

    private getAdobeTag(action: StepperAdobeTag, location: string) {
        return adobeTagging[action] && adobeTagging[action](location);
    }

    private applyAdobeTagging(action: StepperAdobeTag) {
        let tagData = [];

        if (this.currentStepConfig?.adobeLocation) {
            tagData = this.getAdobeTag(action, this.currentStepConfig.adobeLocation);
        }

        if (
            this.currentStepConfig?.customAdobeTags &&
            this.currentStepConfig.customAdobeTags[action]
        ) {
            tagData = this.currentStepConfig.customAdobeTags[action];
        }

        if (!Array.isArray(tagData)) {
            return;
        }

        tagData.forEach((item) => {
            this.adobeLaunchService[item.method].apply(this.adobeLaunchService, item.data);
        });
    }

    openModalDialog(): void {
        this.quitButtonModalDialog.openModalDialog();
    }

    quit(): void {
        this.applyAdobeTagging('onQuit');
        this.exitFlow();
    }

    stayOnCurrentStep(): void {
        this.quitButtonModalDialog.closeDialogModal();
    }

    backButton(): void {
        this.applyAdobeTagging('onBack');
        this.changeStep(-1);
    }

    nextButton(): void {
        this.isWaiting$.next(true);
        this.nextStepClicked.emit();
    }

    contactUs(): void {
        if (this.drawerOpen) {
            this.closeContactUs();
        } else {
            this.openContactUs();
            this.applyAdobeTagging('onContactUs');
        }
    }

    onDrawerStateChange(event: any): void {
        this.drawerOpen = event.isDrawerOpen;
        if (!this.drawerComponent.isModal) {
            this.pushContent = event.isDrawerOpen;
        }
    }

    showBorderOnPinPage(currentStepConfigTitle: string, isMediumView: boolean): boolean {
        const uniquePinPageTitle =
            ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps.lblUniquePin;

        return !isMediumView && currentStepConfigTitle === uniquePinPageTitle;
    }

    private openContactUs(): void {
        const drawerHTMLElement = document.querySelector('aside.gaf-drawer');
        this.renderer.setStyle(drawerHTMLElement, 'height', document.documentElement.scrollHeight + 'px');
        this.drawerComponent.openDrawer();
    }

    private closeContactUs(): void {
        this.drawerComponent.closeDrawer();
    }

    private exitFlow(): void {
        this.store.dispatch(resetGrantRevokePermissionState());
        this.router.navigate([APPLICATION_ROUTES.OWN_ACCOUNTS]);
    }

    private updateCurrentStepConfigAdobeLocation(value: string | null): void {
        this.currentStepConfig.adobeLocation = value;
    }
}
